import React from 'react';
import styled from 'styled-components';
import { FaTrash, FaShoppingCart, FaArrowLeft, FaPlus, FaMinus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Mainlayout from '../../components/Mainlayout';
import { useCart } from '../../store/CartContext';
import MyButton from '../../components/button/MyButton';
import { rgbShift } from 'three/examples/jsm/tsl/display/RGBShiftNode.js';

// Styled Components
const CartContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const CartTitle = styled.h1`
  font-size: 1.6rem;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CartItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr auto auto;
  gap: 20px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 15px;
    padding: 15px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ItemImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
  
  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
    margin: 0 auto;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemName = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ItemCategory = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ItemPrice = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  color: #007bff;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: right;
  }
  
  @media (max-width: 480px) {
    text-align: center;
  }
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #c82333;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
    justify-self: end;
  }
  
  @media (max-width: 480px) {
    justify-self: center;
    margin-top: 10px;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyCartIcon = styled.div`
  font-size: 4rem;
  color: #ccc;
  margin-bottom: 20px;
`;

const EmptyCartText = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 10px;
`;

const EmptyCartSubtext = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 30px;
`;

const ContinueShoppingButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #0056b3;
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
  }
`;

const CartSummary = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-top: 30px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const SummaryTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const SummaryTotal = styled(SummaryRow)`
  font-weight: bold;
  font-size: 1.2rem;
  color: #007bff;
  border-top: 2px solid #f0f0f0;
  padding-top: 15px;
  margin-top: 15px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const QuantityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
  
  @media (max-width: 768px) {
    justify-content: center;
    margin: 15px 0;
  }
`;

const QuantityButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
`;

const QuantityDisplay = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 16px;
  min-width: 50px;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  
  @media (max-width: 768px) {
    min-width: 70px;
    padding: 10px 20px;
    font-size: 18px;
  }
`;

const QuantityLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Cart = () => {
  const { cartItems, removeFromCart, incrementQuantity, decrementQuantity, getCartItemsCount } = useCart();
  const navigate = useNavigate();

  // Console log cart items
  console.log('Cart Items:', cartItems);

  const getTotalItems = () => {
    return getCartItemsCount();
  };

  const handleContinueShopping = () => {
    navigate('/');
  };

  if (cartItems.length === 0) {
    return (
      <Mainlayout>
        <CartContainer>
          <CartHeader>
            <CartTitle>
              <FaShoppingCart />
              Shopping Cart
            </CartTitle>
            
          </CartHeader>
          
          <EmptyCart>
            <EmptyCartIcon>
              <FaShoppingCart />
            </EmptyCartIcon>
            <EmptyCartText>Your cart is empty</EmptyCartText>
            <EmptyCartSubtext>
              Looks like you haven't added any products to your cart yet.
            </EmptyCartSubtext>
            <MyButton
              name="Start Shopping"
              onClick={handleContinueShopping}
              style={{
                backgroundColor: window.ENV.BUTTON.BACKGROUND_COLOR,
                color: 'white',
                fontSize: 14,
                minWidth: 120,
                padding: '0 20px',
                borderRadius: 8,
                marginTop: 16,
              }}
            />
          </EmptyCart>
        </CartContainer>
      </Mainlayout>
    );
  }

  return (
    <Mainlayout>
      <CartContainer>
        <CartHeader>
          <CartTitle>
            <FaShoppingCart />
            Shopping Cart ({getTotalItems()} items)
          </CartTitle>
          <MyButton
            name={<><FaArrowLeft style={{ marginRight: 8 }} />Continue Shopping</>}
            onClick={handleContinueShopping}
            style={{
              backgroundColor: window.ENV.BUTTON.BACKGROUND_COLOR,
              color: 'white',
              fontSize: 14,
              padding: '0 20px',
              borderRadius: 8,
              marginLeft: 8,
            }}
          />
        </CartHeader>

                <CartItemsContainer>
          {cartItems.map((item, index) => (
            <CartItem key={index}>
              <ItemImage 
                src={item.imageUrl && item.imageUrl.trim() !== '' ? `${window.ENV?.SERVER_URL}${item.imageUrl}` : '/src/images/img1.png'} 
                alt={item.productName}
                style={{ cursor: 'pointer' }}
                onError={(e) => {
                  console.log('Image failed to load:', e.target.src);
                  e.target.src = '/src/images/img1.png';
                }}
                onClick={() => navigate(
    `/category/${encodeURIComponent(item.categoryName)}/productdetails/${encodeURIComponent(item.productId)}`
  )}
                // category/double_taper_short_locators_s/productdetails/DTLS-0-DB
              />
              <ItemDetails>
                <ItemName>{item.productName}</ItemName>
                <ItemCategory>Category: {item.categoryName}</ItemCategory>
                <QuantityContainer>
                  <QuantityLabel>Quantity:</QuantityLabel>
                  <MyButton
                    name={<FaMinus size={12} />}
                    onClick={() => decrementQuantity(item.productId)}
                    disabled={(item.quantity || 1) <= 1}
                    style={{
                      backgroundColor: window.ENV.PRODUCTDETAIL_TAB_BOARDER,
                      color: 'white',
                       width: 25,
                      minWidth: 20,
                      height: 25,
                      borderRadius: '50%',
                      fontSize: 16,
                      margin: '0 2px',
                      padding: 0,
                    }}
                  />
                  <QuantityDisplay>{item.quantity || 1}</QuantityDisplay>
                  <MyButton
                    name={<FaPlus size={12} />}
                    onClick={() => incrementQuantity(item.productId)}
                    style={{
                      backgroundColor: window.ENV.PRODUCTDETAIL_TAB_BOARDER,
                      color: 'white',
                      width: 25,
                      minWidth: 20,
                      height: 25,
                      borderRadius: '50%',
                      fontSize: 16,
                      margin: '0 2px',
                      padding: 0,
                    }}
                  />
                </QuantityContainer>
              </ItemDetails>
             
              <MyButton
                name={<><FaTrash style={{ marginRight: 6 }} />Remove</>}
                onClick={() => removeFromCart(item.productId)}
                style={{
                  backgroundColor: window.ENV.BUTTON.BACKGROUND_COLOR,
                  color: 'white',
                  minWidth: 80,
                  fontSize: 14,
                  borderRadius: 6,
                  marginLeft: 8,
                  padding: '0 16px',
                }}
              />
            </CartItem>
          ))}
        </CartItemsContainer>

        <CartSummary>
          <SummaryTitle>Cart Summary</SummaryTitle>
          <SummaryRow>
            <span>Total Products:</span>
            <span>{cartItems.length}</span>
          </SummaryRow>
          <SummaryRow>
            <span>Total Items:</span>
            <span>{getTotalItems()}</span>
          </SummaryRow>
          <SummaryTotal>
            <span>Total Quantity:</span>
            <span>{getTotalItems()}</span>
          </SummaryTotal>
        </CartSummary>
      </CartContainer>
    </Mainlayout>
  );
};

export default Cart; 